import type { RoleKey } from '@/features/auth'

/** Rôles métier proposés à la création et au changement de rôle (rapprochés des rôles V3 existants). */
export const employeeBusinessRoles = ['ADMINISTRATOR', 'DISPATCHER', 'OPERATOR', 'FIELD_MARKER'] as const
export type EmployeeBusinessRole = (typeof employeeBusinessRoles)[number]

export type EmployeeAccountStatus = 'ACTIVE' | 'DISABLED' | 'NO_ACCOUNT'

export interface EmployeeRouteAssignment {
  id: string
  name: string
  color: string | null
  season: string | null
  /** Faux si la route a été supprimée ou n'appartient plus à la saison courante. */
  available: boolean
}

export interface EmployeeEquipmentAssignment {
  id: string
  number: string | null
  name: string
  status: string
  active: boolean
  /** Faux si l'équipement est inactif, en entretien, brisé ou supprimé. */
  available: boolean
}

export interface EmployeeListItem {
  /** Clé de ligne : un compte sans fiche ou une fiche sans compte reste identifiable. */
  key: string
  userId: string | null
  employeeId: string | null
  prenom: string | null
  nom: string | null
  displayName: string
  identifier: string | null
  identityLocked: boolean
  identityToComplete: boolean
  roles: RoleKey[]
  status: EmployeeAccountStatus
  mustChangePassword: boolean
  lastLoginAt: string | null
  route: EmployeeRouteAssignment | null
  equipment: EmployeeEquipmentAssignment | null
}

export interface EquipmentItem {
  id: string
  number: string | null
  name: string
  category: string | null
  status: string
  active: boolean
  available: boolean
}

/** Route d'une liste ordonnée (rang 1 = route principale, `employees.assigned_route_id`). */
export interface EmployeeRankedRoute {
  rank: number
  id: string
  name: string
  color: string | null
  season: string
  available: boolean
}

export interface EmployeeRoutes {
  employeeId: string
  routes: EmployeeRankedRoute[]
}

/** Nouvel ordre après Monter / Descendre (indices bornés : aucun effet hors de la liste). */
export function moveRoute(ids: readonly string[], index: number, delta: -1 | 1): string[] {
  const target = index + delta
  if (index < 0 || index >= ids.length || target < 0 || target >= ids.length) return [...ids]
  const next = [...ids]
  const moved = next[index]
  const other = next[target]
  if (moved === undefined || other === undefined) return next
  next[index] = other
  next[target] = moved
  return next
}

export interface AssignableRoute {
  id: string
  name: string
  color: string | null
}

export type AssignmentState =
  'NOT_APPLICABLE' | 'COMPLETE' | 'MISSING_ROUTE' | 'MISSING_EQUIPMENT' | 'MISSING_BOTH' | 'TO_FIX'

/**
 * État des affectations d'un opérateur. Une ressource devenue indisponible n'est jamais remplacée ni masquée :
 * l'affectation passe « à corriger ». Les affectations ne servent que de valeurs par défaut aux prochaines
 * missions; elles ne bloquent pas la connexion.
 */
export function assignmentState(
  employee: Pick<EmployeeListItem, 'roles' | 'route' | 'equipment'>,
): AssignmentState {
  if (!employee.roles.includes('OPERATOR')) return 'NOT_APPLICABLE'
  if ((employee.route && !employee.route.available) || (employee.equipment && !employee.equipment.available))
    return 'TO_FIX'
  if (!employee.route && !employee.equipment) return 'MISSING_BOTH'
  if (!employee.route) return 'MISSING_ROUTE'
  if (!employee.equipment) return 'MISSING_EQUIPMENT'
  return 'COMPLETE'
}

export const assignmentStateLabels: Record<AssignmentState, string> = {
  NOT_APPLICABLE: '—',
  COMPLETE: 'Affectations complètes',
  MISSING_ROUTE: 'Route à assigner',
  MISSING_EQUIPMENT: 'Équipement à assigner',
  MISSING_BOTH: 'Route et équipement à assigner',
  TO_FIX: 'Affectation à corriger',
}

export const accountStatusLabels: Record<EmployeeAccountStatus, string> = {
  ACTIVE: 'Actif',
  DISABLED: 'Désactivé',
  NO_ACCOUNT: 'Sans compte',
}

export const equipmentStatusLabels: Record<string, string> = {
  disponible: 'Disponible',
  en_operation: 'En opération',
  entretien: 'En entretien',
  brise: 'Brisé',
}

/** Rôle principal affiché : le rôle métier s'il existe, sinon le premier rôle attribué. */
export function primaryRole(roles: readonly RoleKey[]): RoleKey | null {
  return employeeBusinessRoles.find((role) => roles.includes(role)) ?? roles[0] ?? null
}

/** Autres employés qui partagent la même route ou le même tracteur (quarts différents : aucune exclusivité). */
export function sharedAssignments(employee: EmployeeListItem, employees: readonly EmployeeListItem[]) {
  const others = employees.filter((other) => other.key !== employee.key)
  return {
    route: employee.route ? others.filter((other) => other.route?.id === employee.route?.id) : [],
    equipment: employee.equipment
      ? others.filter((other) => other.equipment?.id === employee.equipment?.id)
      : [],
  }
}

// ------------------------------------------------------------------ Commandes

export interface CreateEmployeeCommand {
  idempotencyKey: string
  prenom: string
  nom: string
  identifier: string
  roleKey: EmployeeBusinessRole
  routeId: string | null
  equipmentId: string | null
  temporaryPassword: string
}

export interface IdentifierAvailability {
  identifier: string
  available: boolean
  suggestion: string | null
}

export type AssignmentKind = 'ROUTE' | 'EQUIPMENT'

// ------------------------------------------------------------------ Journaux

export type AccessEventType =
  'LOGIN_SUCCEEDED' | 'LOGIN_FAILED' | 'LOGOUT' | 'PASSWORD_CHANGED' | 'PASSWORD_TEMP_REPLACED'

export interface AccessLogEntry {
  id: string
  occurredAt: string
  userId: string | null
  identifier: string | null
  accountName: string | null
  eventType: AccessEventType
  success: boolean
  userAgent: string | null
  source: 'APPLICATION' | 'LOGIN_SCREEN'
}

export interface AccessLogFilters {
  from?: string
  to?: string
  userId?: string
  result?: 'SUCCESS' | 'FAILURE'
  limit: number
  offset: number
}

export interface AccessLogPage {
  total: number
  items: AccessLogEntry[]
}

export const accessEventLabels: Record<AccessEventType, string> = {
  LOGIN_SUCCEEDED: 'Connexion',
  LOGIN_FAILED: 'Tentative de connexion refusée',
  LOGOUT: 'Déconnexion',
  PASSWORD_CHANGED: 'Mot de passe modifié',
  PASSWORD_TEMP_REPLACED: 'Mot de passe temporaire remplacé',
}

export interface EmployeeHistoryEntry {
  id: string
  occurredAt: string
  type: string
  details: Record<string, unknown>
  actorName: string | null
}

export const historyTypeLabels: Record<string, string> = {
  EMPLOYEE_CREATED: 'Compte créé',
  IDENTITY_COMPLETED: 'Identité complétée',
  ROLE_CHANGED: 'Rôle modifié',
  ACCOUNT_DISABLED: 'Compte désactivé',
  ACCOUNT_ENABLED: 'Compte réactivé',
  PASSWORD_RESET: 'Mot de passe réinitialisé, sessions révoquées',
  SESSIONS_REVOKED: 'Toutes les sessions déconnectées',
  ROUTE_CHANGED: 'Route assignée modifiée',
  EQUIPMENT_CHANGED: 'Équipement assigné modifié',
}

// ------------------------------------------------------------------ Rôles et permissions

export interface RolePermissionsMatrix {
  roles: { key: string; label: string; description: string; permissions: string[]; memberCount: number }[]
  permissions: { key: string; description: string }[]
  protectedPermissions: string[]
}

/** Consultation (`*.read`) ou modification (toute autre action). */
export function permissionAccess(permissionKey: string): 'READ' | 'WRITE' {
  return permissionKey.endsWith('.read') ? 'READ' : 'WRITE'
}

// ------------------------------------------------------------------ Erreurs

const errorMessages: Record<string, string> = {
  FORBIDDEN: 'Vous n’avez pas le droit d’effectuer cette action.',
  SELF_FORBIDDEN: 'Cette action ne peut pas viser votre propre compte.',
  IDENTIFIER_TAKEN: 'Cet identifiant vient d’être pris. Vérifiez la proposition et réessayez.',
  IDENTIFIER_MISMATCH: 'L’identifiant ne correspond pas au prénom et au nom saisis.',
  IDENTITY_INVALID: 'Le prénom et le nom sont obligatoires.',
  IDENTITY_NAMES_IDENTICAL: 'Le nom doit être distinct du prénom.',
  IDENTITY_LOCKED: 'Le prénom, le nom et l’identifiant ne peuvent plus être modifiés.',
  ROLE_INVALID: 'Ce rôle ne peut pas être attribué ici.',
  ROUTE_UNAVAILABLE: 'Cette route n’est pas disponible pour la saison courante.',
  ROUTE_DUPLICATE: 'Cette route est déjà dans la liste.',
  ROUTE_NOT_FOUND: 'Cette route n’existe plus.',
  ROUTE_LIST_TOO_LONG: 'Une liste compte au plus 30 routes.',
  EQUIPMENT_UNAVAILABLE: 'Cet équipement est inactif, en entretien ou indisponible.',
  EMPLOYEE_NOT_FOUND: 'Cet employé est introuvable dans votre organisation.',
  LAST_ADMINISTRATOR: 'Il doit rester au moins un administrateur actif.',
  PROTECTED_PERMISSION: 'Cette permission est indispensable à l’administration et ne peut pas être retirée.',
  WEAK_PASSWORD: 'Le mot de passe temporaire n’est pas assez robuste. Générez-en un nouveau.',
  PROVIDER_UNAVAILABLE:
    'Le service d’authentification n’a pas répondu. Réessayez : la création reprendra sans doublon.',
  COMPLETION_FAILED: 'Le compte n’a pas pu être finalisé. Réessayez : la création reprendra sans doublon.',
  EQUIPMENT_NUMBER_TAKEN: 'Ce numéro d’équipement existe déjà.',
  EQUIPMENT_INVALID: 'Le nom de l’équipement est obligatoire.',
  CURRENT_PASSWORD_INVALID: 'Le mot de passe temporaire saisi est incorrect.',
  PASSWORD_UNCHANGED: 'Le nouveau mot de passe doit être différent du mot de passe temporaire.',
  PASSWORD_CHANGE_NOT_REQUIRED: 'Votre mot de passe a déjà été remplacé. Reconnectez-vous.',
}

export class EmployeeCommandError extends Error {
  constructor(public readonly code: string) {
    super(errorMessages[code] ?? 'L’opération n’a pas pu être terminée. Réessayez dans un instant.')
    this.name = 'EmployeeCommandError'
  }
}

/** Code métier (`raise exception 'CODE'`) extrait d'une erreur Supabase, sinon `UNEXPECTED`. */
export function commandErrorCode(message: string | undefined): string {
  const code = message?.trim() ?? ''
  return /^[A-Z][A-Z_]+$/.test(code) ? code : 'UNEXPECTED'
}
